import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { Download, FileText, ImageIcon, Loader2, Send, Sparkles, Upload as UploadIcon, X } from "lucide-react";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import { downloadDataUrl, slugify } from "./imageCreationService";
import { EXTRACTION_PDF_MAX_BYTES, productNameFrom, productNamesFrom, runImageExtraction } from "./imageExtractionService";

// Image Extraction — upload a PDF, say which product you want (e.g. "extract
// product image for Rico Rice from attached PDF and enhance them with sharper
// detail and a clean white background"). The product's own picture is taken
// from the PDF at its native resolution and ENHANCED (no new image is
// generated): solid background → pure white, sharper, upscaled if small. Connected to the Image Creation card by
// WorkflowSection through onSendToImageCreation / pendingImages, like Menu Data
// Extraction → Image Creation. Results live in this card only; nothing is saved.

const MAX_PROMPT = 500;
const EXAMPLE_PROMPT =
  "Extract product image for Rico Rice from attached PDF and enhance them with sharper detail and a clean white background";

const inputCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-50";

const sameFile = (a, b) => !!a && !!b && a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

export default function ImageExtraction({
  onRemove,
  onSendToImageCreation,
  hasImageCreationCard,
  pendingProducts = [],
  onConsumePendingProduct,
  autoSendToNext = false, // Workflow tab: pass the found picture straight on to Image Creation
  onProgress, // Workflow tab: { type: "found" | "not-found" | "no-pdf" | "error", name, detail }
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [status, setStatus] = useState(""); // non-empty while processing
  const [results, setResults] = useState(null); // null = nothing run yet
  const [emptyMessage, setEmptyMessage] = useState("");
  const [missingNote, setMissingNote] = useState(""); // named products that had no picture, when others did
  const [foundIn, setFoundIn] = useState(""); // which PDF the pictures came from, when more than one was searched
  const [selected, setSelected] = useState({}); // index -> true
  const processing = !!status;
  // Price / description of the menu item being looked up, so they travel on to Image Creation with the picture.
  const detailsRef = useRef(null);

  // Object URLs for the exact PDF images live as long as their results do.
  const resultsRef = useRef(null);
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);
  const revokeUrls = (list) => (list || []).forEach((r) => r?.exact?.url && URL.revokeObjectURL(r.exact.url));
  const releaseExact = () => revokeUrls(resultsRef.current);
  useEffect(() => () => revokeUrls(resultsRef.current), []);

  // A different store means different context; start clean.
  useEffect(() => {
    const reset = () => {
      setResults(null);
      setSelected({});
      setEmptyMessage("");
      setMissingNote("");
    };
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, reset);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, reset);
  }, []);

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    if (picked.type !== "application/pdf") {
      message.error("Please choose a PDF file.");
      return;
    }
    if (picked.size > EXTRACTION_PDF_MAX_BYTES) {
      message.error("The PDF must be 25 MB or smaller.");
      return;
    }
    setFile(picked);
    releaseExact();
    setResults(null);
    setSelected({});
    setEmptyMessage("");
  };

  // Looks for the product in each PDF in turn and stops at the first one that has it.
  // single: the request is exactly one product (e.g. a menu item), so "and" / "&" in its name is not a list.
  const runSearch = async (sources, request, { auto = false, single = false } = {}) => {
    setResults(null);
    setSelected({});
    setEmptyMessage("");
    setMissingNote("");
    setFoundIn("");
    releaseExact();
    const errors = [];
    try {
      for (const source of sources) {
        try {
          const label = sources.length > 1 ? ` in ${source.name}` : "";
          const { results: found, missing = [] } = await runImageExtraction({ file: source, prompt: request, single, onStatus: (s) => setStatus(`${s}${label}`) });
          if (found.length === 0) continue;
          setResults(found);
          setMissingNote(missing.length ? `No picture found for ${missing.map((n) => `“${n}”`).join(", ")} in this PDF.` : "");
          setFoundIn(sources.length > 1 ? source.name : "");
          const preselected = {};
          found.forEach((r, i) => {
            if (r.enhancedDataUrl && r.verified !== "unsure") preselected[i] = true; // a picture that couldn't be confirmed is never ticked for you
          });
          setSelected(preselected);
          onProgress?.({ type: "found", name: productNameFrom(request), detail: source.name });
          // One picture per named product (the best one), in the order the customer named them.
          const bestPerItem = [];
          const seenItems = new Set();
          found.forEach((r) => {
            const key = String(r.item || r.label || "").toLowerCase();
            if (r.enhancedDataUrl && r.verified !== "unsure" && !seenItems.has(key)) {
              seenItems.add(key);
              bestPerItem.push(r);
            }
          });
          const unsureOnly = found.filter((r) => r.enhancedDataUrl && r.verified === "unsure" && !seenItems.has(String(r.item || r.label || "").toLowerCase()));
          if (auto && unsureOnly.length) {
            message.warning(`Couldn't confirm the picture for ${unsureOnly.map((r) => `“${r.label}”`).join(", ")}, so it wasn't sent on automatically. Check it in Image Extraction.`);
          }
          if (auto && bestPerItem.length && onSendToImageCreation && hasImageCreationCard) {
            onSendToImageCreation(
              bestPerItem.map((r, i) => ({ key: `extracted-${Date.now()}-${i}`, name: r.label || productNameFrom(request), dataUrl: r.enhancedDataUrl, ...detailsFor(request) }))
            );
            message.success(bestPerItem.length === 1 ? "Found the picture and sent it to Image Creation." : `Found ${bestPerItem.length} pictures and sent them to Image Creation.`);
          }
          return;
        } catch (err) {
          errors.push(err.message || "Couldn't read that PDF.");
        }
      }
      // Not found anywhere.
      if (errors.length === sources.length) {
        onProgress?.({ type: "error", name: productNameFrom(request), detail: errors[0] });
        return message.error(errors[0]);
      }
      const names = sources.map((s) => `“${s.name}”`).join(" or ");
      setResults([]);
      const asked = single ? [productNameFrom(request)] : productNamesFrom(request);
      setEmptyMessage(
        `We couldn't find an image of ${asked.map((n) => `“${n}”`).join(" or ")} in ${names}. Check the product ${asked.length > 1 ? "names" : "name"}, or upload the PDF that contains ${asked.length > 1 ? "them" : "it"}.`
      );
      onProgress?.({ type: "not-found", name: productNameFrom(request), detail: sources.map((s) => s.name).join(", ") });
    } finally {
      setStatus("");
    }
  };

  const handleExtract = async () => {
    const request = prompt.trim();
    if (!file) return message.warning("Upload a PDF first.");
    if (!request) return message.warning("Tell me which product to extract.");
    await runSearch([file], request);
  };

  // price / description of the menu item, when this search came from Menu Data Extraction for the same product
  const detailsFor = (request) => {
    const d = detailsRef.current;
    if (!d || productNameFrom(request).toLowerCase() !== d.name.toLowerCase()) return {};
    return { ...(d.price !== "" && d.price != null ? { price: d.price } : {}), ...(d.description ? { description: d.description } : {}) };
  };

  // A product sent from Menu Data Extraction: search this card's PDF and the menu's PDF (if it is one).
  useEffect(() => {
    if (pendingProducts.length === 0 || processing) return;
    const item = pendingProducts[0];
    onConsumePendingProduct?.();
    const name = String(item.name || "").trim().slice(0, MAX_PROMPT);
    if (!name) return;
    setPrompt(name);
    detailsRef.current = { name, price: item.price ?? "", description: item.description || "" };
    const sources = [];
    if (file) sources.push(file);
    if (item.file && !sameFile(file, item.file)) sources.push(item.file);
    if (!file && item.file) setFile(item.file); // show the shared PDF in this card
    if (sources.length === 0) {
      message.info(`“${name}” received from Menu Data Extraction. Upload the PDF that has its picture, then press Extract & Enhance.`);
      onProgress?.({ type: "no-pdf", name });
      return;
    }
    message.info(`Looking for “${name}” in ${sources.map((s) => s.name).join(" and ")}…`);
    runSearch(sources, name, { auto: autoSendToNext, single: true }); // a menu item is one product
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProducts, processing]);

  const selectedItems = () =>
    (results || [])
      .map((r, i) => ({ r, i }))
      .filter(({ r, i }) => selected[i] && r.enhancedDataUrl)
      .map(({ r, i }) => ({
        key: `extracted-${Date.now()}-${i}`,
        name: r.label || "Extracted product",
        dataUrl: r.enhancedDataUrl,
        ...detailsFor(prompt),
      }));

  const handleSend = () => {
    const items = selectedItems();
    if (!items.length) return message.warning("Select at least one enhanced image first.");
    onSendToImageCreation?.(items);
    message.success(
      items.length === 1 ? "Sent to Image Creation." : `${items.length} images sent to Image Creation (loaded one at a time).`
    );
  };

  const canSend = !!onSendToImageCreation && hasImageCreationCard;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
            <ImageIcon size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Image Extraction</h3>
            <p className="text-xs text-gray-500">Pull a product photo out of a PDF and place it on a clean white background</p>
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={processing}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50"
          >
            Remove from Components
          </button>
        )}
      </header>

      <div className="flex flex-col gap-5 p-4 sm:p-5">
        {/* ---------- Upload ---------- */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-900">PDF</span>
          {file ? (
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-2.5">
              <div className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-white text-indigo-600">
                <FileText size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
                <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  releaseExact();
                  setResults(null);
                  setSelected({});
                  setEmptyMessage("");
                }}
                disabled={processing}
                aria-label="Remove file"
                className="flex-none rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center transition-colors hover:border-indigo-400"
            >
              <UploadIcon size={22} className="text-indigo-600" />
              <span className="text-sm font-medium text-gray-900">Click to upload a PDF</span>
              <span className="text-xs text-gray-500">Up to 20 pages, 25 MB</span>
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="application/pdf" hidden onChange={handleFileChange} />
        </div>

        {/* ---------- Request ---------- */}
        <div className="flex flex-col gap-2">
          <label htmlFor="image-extraction-prompt" className="text-sm font-medium text-gray-900">
            Which product?
          </label>
          <textarea
            id="image-extraction-prompt"
            rows={3}
            maxLength={MAX_PROMPT}
            value={prompt}
            disabled={processing}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Type the product name to extract"
            className={`${inputCls} resize-y`}
          />
          <button
            type="button"
            disabled={processing}
            onClick={() => setPrompt(EXAMPLE_PROMPT)}
            className="self-start rounded-full border border-gray-200 px-3 py-1 text-left text-xs text-gray-600 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-50"
          >
            Use example: “{EXAMPLE_PROMPT.slice(0, 48)}…”
          </button>
          <p className="text-xs text-gray-500">
            The picture from your PDF is enhanced with AI — the same product with sharper detail, better lighting and a clean white background. It can take up to a minute per picture.
          </p>
        </div>

        <button
          type="button"
          onClick={handleExtract}
          disabled={processing || !file || !prompt.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {processing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {processing ? status : "Extract & Enhance"}
        </button>

        {/* ---------- Results ---------- */}
        {results && results.length === 0 && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{emptyMessage}</p>
        )}

        {results && results.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">
                {results.length} {results.length === 1 ? "image" : "images"} found{foundIn ? ` in “${foundIn}”` : ""}
              </span>
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend || selectedCount === 0}
                title={canSend ? "" : "Add the Image Creation component to your Components first"}
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send size={13} /> Send to Image Creation
              </button>
            </div>
            {missingNote && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{missingNote}</p>}
            {!canSend && (
              <p className="text-xs text-gray-500">Add the Image Creation component from the Library to send images there.</p>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {results.map((r, i) => {
                const name = r.label || "Extracted product";
                const ex = r.exact || null;
                const originalSrc = ex?.url || r.originalDataUrl;
                return (
                  <div key={i} className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-900">
                        <input
                          type="checkbox"
                          checked={!!selected[i]}
                          disabled={!r.enhancedDataUrl}
                          onChange={(e) => setSelected((prev) => ({ ...prev, [i]: e.target.checked }))}
                        />
                        <span className="truncate">{name}</span>
                      </label>
                      <span className="flex-none text-xs text-gray-500">Page {r.page}</span>
                    </div>

                    {r.enhancedDataUrl ? (
                      <div className="grid grid-cols-2 gap-2">
                        <figure className="flex flex-col gap-1">
                          <div className="grid aspect-square place-items-center overflow-hidden rounded-lg bg-gray-100">
                            <img src={originalSrc} alt={`${name} original`} className="max-h-full max-w-full object-contain" />
                          </div>
                          <figcaption className="text-center text-xs text-gray-500">
                            {ex ? `Original from PDF (${ex.width}×${ex.height})` : "Original (page crop)"}
                          </figcaption>
                        </figure>
                        <figure className="flex flex-col gap-1">
                          <div className="grid aspect-square place-items-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                            <img src={r.enhancedDataUrl} alt={`${name} enhanced`} className="max-h-full max-w-full object-contain" />
                          </div>
                          <figcaption className="text-center text-xs text-gray-500">Enhanced</figcaption>
                        </figure>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {r.originalDataUrl && (
                          <img src={r.originalDataUrl} alt={`${name} original`} className="max-h-40 self-center rounded-lg object-contain" />
                        )}
                        <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{r.error || "Couldn't enhance this image."}</p>
                      </div>
                    )}

                    <div className="flex flex-col gap-1.5">
                      {originalSrc && (
                        <button
                          type="button"
                          onClick={() => downloadDataUrl(originalSrc, `${slugify(name) || "product"}-original.png`)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                        >
                          <Download size={13} /> {ex ? "Download exact image from PDF" : "Download original (page crop)"}
                        </button>
                      )}
                      {r.enhancedDataUrl && (
                        <button
                          type="button"
                          onClick={() => downloadDataUrl(r.enhancedDataUrl, `${slugify(name) || "product"}-white.png`)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-gray-400"
                        >
                          <Download size={13} /> Download enhanced (white background)
                        </button>
                      )}
                      {!ex && r.originalDataUrl && (
                        <p className="text-xs text-gray-500">
                          This picture isn't a separate image inside the PDF (it is part of a scanned or flattened page), so the page crop is the closest original.
                        </p>
                      )}
                      {r.enhancedDataUrl && r.verified === "verified" && (
                        <p className="text-xs text-emerald-700">Checked: this is {r.shows ? `${r.shows}` : "the product you named"}.</p>
                      )}
                      {r.enhancedDataUrl && r.verified === "unsure" && (
                        <p className="text-xs text-amber-700">
                          Couldn't fully confirm this is the right product{r.shows ? ` (it looks like ${r.shows})` : ""}. Please check it before using it.
                        </p>
                      )}
                      {r.enhancedDataUrl && r.fallback && (
                        <p className="text-xs text-amber-700">
                          AI enhancement wasn't available for this picture, so the standard enhance was used (cleaner white background and sharper detail).
                        </p>
                      )}
                      {r.enhancedDataUrl && r.fallback && !r.backgroundReplaced && (
                        <p className="text-xs text-gray-500">
                          The background isn't a single solid colour, so it was kept as is — the image was sharpened and cleaned up only.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
